import { BFF_API_ROUTES } from 'constants/routes';

import { baseRTKApi } from 'shared/api/baseApi';
import type {
  TDadataAutocompleteAddressRequest,
  TDadataFullAddressSelectOption,
  TDadataFullNameSuggestRequest,
  TDadataFullNameSuggestResponse,
  TDadataOrganizationSuggestResponse,
  TDadataSeparateAddressClientRequest,
  TDadataSeparateAddressSelectOption,
} from 'shared/types/TDaData';

export const dadataQueries = baseRTKApi.injectEndpoints({
  endpoints: (build) => ({
    getFullAddressAutocomplete: build.query<TDadataFullAddressSelectOption[], TDadataAutocompleteAddressRequest>({
      async queryFn(params, _queryApi, _extraOptions, fetchQuery) {
        const { search, bound = 'house' } = params;
        if (search.length < 3) {
          return { data: [] };
        }

        const result = await fetchQuery({
          url: BFF_API_ROUTES.getFullAddressesAutocomplete,
          method: 'POST',
          body: { search, bound },
        });

        if (result.error) return { data: [] };
        return { data: result.data as TDadataFullAddressSelectOption[] };
      },
      keepUnusedDataFor: 10 * 60,
      extraOptions: { maxRetries: 1 },
    }),
    getSeparateAddressesAutocomplete: build.query<
      TDadataSeparateAddressSelectOption[],
      TDadataSeparateAddressClientRequest & {
        minLength?: number;
      }
    >({
      async queryFn({ query, regionFiasId, minLength = 1 }, _queryApi, _extraOptions, fetchQuery) {
        if (query.length < minLength) {
          return { data: [] };
        }
        const result = await fetchQuery({
          url: BFF_API_ROUTES.getSeparateAddressesAutocomplete,
          method: 'POST',
          body: { query, regionFiasId },
        });

        if (result.error) return { data: [] };
        return { data: result.data as TDadataSeparateAddressSelectOption[] };
      },
      keepUnusedDataFor: 10 * 60,
      extraOptions: { maxRetries: 1 },
    }),
    getFullNameSuggest: build.query<TDadataFullNameSuggestResponse, TDadataFullNameSuggestRequest>({
      async queryFn({ query }, _queryApi, _extraOptions, fetchQuery) {
        if (!query.length) {
          return {
            data: {
              suggestions: [],
            },
          };
        }

        const result = await fetchQuery({
          url: BFF_API_ROUTES.getFullNameSuggest,
          method: 'POST',
          body: { query },
        });

        if (result.error)
          return {
            data: {
              suggestions: [],
            },
          };

        return { data: result.data as TDadataFullNameSuggestResponse };
      },
      keepUnusedDataFor: 10 * 60,
      extraOptions: { maxRetries: 1 },
    }),
    getOrganizationSuggestions: build.query<TDadataOrganizationSuggestResponse, string>({
      async queryFn(query, _queryApi, _extraOptions, fetchQuery) {
        if (query?.length < 3) {
          return {
            data: [],
          };
        }

        const result = await fetchQuery({
          url: BFF_API_ROUTES.getOrganizationSuggestions,
          method: 'POST',
          body: { query },
        });

        if (result.error)
          return {
            data: [],
          };

        return { data: result.data as TDadataOrganizationSuggestResponse };
      },
      keepUnusedDataFor: 10 * 60,
      extraOptions: { maxRetries: 1 },
    }),
  }),
});

export const useGetFullAddressAutocomplete = dadataQueries.useLazyGetFullAddressAutocompleteQuery;
export const useGetSeparateAddressesAutocomplete = dadataQueries.useLazyGetSeparateAddressesAutocompleteQuery;
export const useGetFullNameSuggest = dadataQueries.useLazyGetFullNameSuggestQuery;
export const useGetOrganizationSuggestions = dadataQueries.useLazyGetOrganizationSuggestionsQuery;
